import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { WithTranslation, withTranslation } from 'react-i18next';
import * as i18next from 'i18next';
import { Button, Input, Select, Switch, Modal, Alert } from 'antd';
import { Loader } from 'Components/Loader/Loader';
import {
    addBrandDataRequest,
    generateFreeRoundsCredentialsRequest,
    saveFreeRoundsCredentialsRequest,
    updateBrandDataRequest,
} from 'redux/actions/configProvider/manage-brand-actions';
import { IBrandData, IOperatorData } from 'helpers/interfaces';
import {
    getFreeRoundsCredentials,
    getFreeRoundsCredentialsLoadingStatus,
    isBrandDataLoading,
} from 'redux/selectors/configProvider/manage-brand-selectors';
import { Messages } from 'utils';
import { CopyOutlined, LoadingOutlined } from '@ant-design/icons';
const { Option } = Select;

interface IProps {
    operatorData: IOperatorData;
    brandData: IBrandData;
    updateBrandData: Function;
    addBrandInfo: Function;
    updateBrandInfo: Function;
    loading: boolean;
    nextStep: Function;
    isExistingBrand?: boolean;
    t: i18next.TFunction;
    generateFreeRoundsCredentials: Function;
    credentialsLoading: boolean;
    freeRoundsCredentials: any;
    saveFreeRoundsCredentials: Function;
}

interface IValues {
    name: string;
    serviceUrl: string;
    serviceLogin: string;
    servicePassword: string;
    inactivityTimerMinutes: string;
    freeRoundsEnabled: boolean;
    buyFeatureEnabled: boolean;
    awardedFeatureEnabled: boolean;
    dynamicGameConfigurationEnabled: boolean;
    geolocationEnabled: boolean;
    singleSessionModeEnabled: boolean;
    directFreeRoundsSupporting: boolean;
    freeRoundPromotionType: number | null;
    gameEventsEnabled: boolean;
}

interface IState {
    values: IValues;
    errors: any;
    username: string;
}

type brandDataKeys =
    | 'name'
    | 'serviceUrl'
    | 'serviceLogin'
    | 'servicePassword'
    | 'inactivityTimerMinutes'
    | 'freeRoundsEnabled'
    | 'buyFeatureEnabled'
    | 'awardedFeatureEnabled'
    | 'dynamicGameConfigurationEnabled'
    | 'geolocationEnabled'
    | 'singleSessionModeEnabled'
    | 'directFreeRoundsSupporting'
    | 'gameEventsEnabled'
    | 'freeRoundPromotionType';
const defaultValues: IValues = {
    name: '',
    serviceUrl: '',
    serviceLogin: '',
    servicePassword: '',
    inactivityTimerMinutes: '30',
    freeRoundsEnabled: false,
    buyFeatureEnabled: false,
    dynamicGameConfigurationEnabled: false,
    geolocationEnabled: false,
    singleSessionModeEnabled: false,
    directFreeRoundsSupporting: false,
    freeRoundPromotionType: null,
    awardedFeatureEnabled: false,
    gameEventsEnabled: false,
};

class StepManageBrandInfo extends React.Component<IProps & WithTranslation, IState> {
    state: IState = {
        values: StepManageBrandInfo.getInitialBrandData(this.props),
        errors: { ...defaultValues, inactivityTimerMinutes: '' },
        username: '',
    };

    	private confirmToggle = (
		key: 'freeRoundsEnabled' | 'awardedFeatureEnabled' | 'gameEventsEnabled',
		label: string,
		nextValue: boolean,
	) => {
		if (nextValue) {
			this.setState((state) => ({
				values: { ...state.values, [key]: nextValue },
			}));
			return;
		}

		const content = `You are now disabling ${label} for this brand.`;
		let additionalContent = null;
		
		if (key === 'gameEventsEnabled') {
			additionalContent = (
				<>
                    <Alert 
                        className='mt-4'
                        showIcon 
                        message="All active event streams will be halted, and they will restart once reactivated."
                        type="warning" 
                    />
                    <Alert 
                        className='mt-4'
                        showIcon 
                        message="Please ensure that necessary actions have been taken to achieve the desired outcome before deactivating."
                        type="warning" 
                    />
				</>
			);
		} else if (key === 'freeRoundsEnabled') {
			additionalContent = (
				<>
					<Alert 
                        className='mt-4'
                        showIcon 
                        message="All active Free Round campaigns will be halted, and they will restart once reactivated."
                        type="warning" 
                    />
                    <Alert 
                        className='mt-4'
                        showIcon 
                        message="Please ensure that necessary actions have been taken to achieve the desired outcome before deactivating."
                        type="warning" 
                    />
				</>
			);
		} else if (key === 'awardedFeatureEnabled') {
			additionalContent = (
				<>
					<Alert 
                        className='mt-4'
                        showIcon 
                        message="All active Awarded Features will be halted, and they will restart once reactivated."
                        type="warning" 
                    />
                    <Alert 
                        className='mt-4'
                        showIcon 
                        message="Please ensure that necessary actions have been taken to achieve the desired outcome before deactivating."
                        type="warning" 
                    />
				</>
			);
		}

		const footerNote = (
			<div className='mt-4'>
				This change will be applied when you save the form.
			</div>
		);

		Modal.warning({
			title: `Disable ${label}`,
            icon: null,
			content: (
				<div>
					{content}
					{additionalContent}
					{footerNote}
				</div>
			),
			okText: 'I understand',
			okType: 'danger',
            okButtonProps: {
                shape: 'round',
            },
			onOk: () =>
				this.setState((state) => ({
					values: { ...state.values, [key]: nextValue },
				})),
		});
	};

    static getInitialBrandData = (props: IProps) => {
        const { brandData, operatorData, updateBrandData } = props;
        const brandDataFromUrl = operatorData.brands?.filter(
            (brand: IBrandData) => brand.id === brandData.id,
        );

        brandDataFromUrl && brandDataFromUrl.length && updateBrandData(brandDataFromUrl[0]);

        return brandDataFromUrl && brandDataFromUrl.length
            ? {
                  ...brandData,
                  ...brandDataFromUrl[0],
              }
            : {
                  ...brandData,
              };
    };

    generatePassword = () => {
        const { generateFreeRoundsCredentials, brandData } = this.props;

        generateFreeRoundsCredentials({ brandId: brandData.id, username: this.state.username });
    };

    copyToClipboard = () => {
        Messages.success(this.props.t('copied_to_clipboard'));

        navigator.clipboard.writeText(this.props.freeRoundsCredentials.password);
    };

    savePassword = () => {
        this.props.saveFreeRoundsCredentials(this.props.freeRoundsCredentials.id);
    };

    onChange = (e: React.ChangeEvent<HTMLInputElement>, key: brandDataKeys) => {
        const { value } = e.target;

        this.setState((state) => ({
            values: { ...state.values, [key]: value.trim() },
            errors: { ...state.errors, [key]: '' },
        }));
    };

    handleSubmit = () => {
        if (this.isDataReady() && this.isDataValid()) {
            const { values } = this.state;
            const { operatorData, brandData } = this.props;
            const { brands } = operatorData;
            const { id, operatorId } = brandData;
            const { enabled } = brandData;

            this.props.updateBrandData({ ...values });

            if (brands && brands.length) {
                this.props.updateBrandInfo({
                    ...values,
                    id,
                    operatorId,
                    enabled,
                });
            } else {
                this.props.addBrandInfo({
                    operatorId: this.props.brandData.operatorId,
                    ...values,
                });
            }
        }
    };

    isDataValid = () => {
        const errors = this.getValidationErrors();

        return Object.values(errors).every((error: any) => error === '');
    };

    getValidationErrors = () => {
        const { name, serviceUrl, serviceLogin, servicePassword, inactivityTimerMinutes } =
            this.state.values;
        const errors = {
            name:
                name.length > 2 && name.length <= 64
                    ? ''
                    : 'this_field_should_be_between_2_and_64_chars',
            serviceUrl: serviceUrl.length ? '' : 'this_field_cannot_be_empty',
            serviceLogin: serviceLogin.length ? '' : 'this_field_cannot_be_empty',
            servicePassword: servicePassword.length > 2 ? '' : 'password_validation_error_3',
            inactivityTimerMinutes:
                Number(inactivityTimerMinutes) > 0 && Number(inactivityTimerMinutes) < 121
                    ? ''
                    : 'inactivityTimerMinutes_should_be_between_0_and_120',
            freeRoundsEnabled: '',
            buyFeatureEnabled: '',
        };

        this.setState({ errors });

        return errors;
    };

    onChangeBrand = (brandName: string) => {
        const brandData = this.props.operatorData.brands?.filter(
            (brand: IBrandData) => brand.name === brandName,
        )[0];

        if (brandData) {
            const {
                name,
                serviceUrl,
                serviceLogin,
                servicePassword,
                inactivityTimerMinutes,
                freeRoundsEnabled,
                buyFeatureEnabled,
                awardedFeatureEnabled,
                dynamicGameConfigurationEnabled,
                geolocationEnabled,
                singleSessionModeEnabled,
                directFreeRoundsSupporting,
                freeRoundPromotionType,
                gameEventsEnabled,
            } = brandData;

            this.setState({
                values: {
                    name,
                    serviceUrl,
                    serviceLogin,
                    servicePassword,
                    inactivityTimerMinutes: inactivityTimerMinutes || '30',
                    freeRoundsEnabled,
                    buyFeatureEnabled,
                    awardedFeatureEnabled,
                    dynamicGameConfigurationEnabled,
                    geolocationEnabled,
                    singleSessionModeEnabled,
                    directFreeRoundsSupporting,
                    freeRoundPromotionType,
                    gameEventsEnabled,
                },
            });

            this.props.updateBrandData(brandData);
        }
    };

    nextStep = () => this.props.nextStep();

    isDataReady = () => {
        const { isExistingBrand, brandData } = this.props;
        const isFieldsNotEmpty = Object.entries(this.state.values).every((value: string[]) => {
            if (typeof value[1] === 'boolean') {
                return true;
            }
            return value[1] !== '';
        });

        return isExistingBrand ? brandData.id && isFieldsNotEmpty : isFieldsNotEmpty;
    };

    onInactivityTimerChange = (value: string) => {
        this.setState((state) => ({
            values: { ...state.values, inactivityTimerMinutes: value },
        }));
    };

    onFreeRoundsSupportChange = (value: boolean) => {
        this.confirmToggle('freeRoundsEnabled', 'Free Rounds', value);
    };

    onAwardedFeatureSupportChange = (value: boolean) => {
        this.confirmToggle('awardedFeatureEnabled', 'Awarded Feature', value);
    };

    onBuyFeatureSupportChange = (value: boolean) => {
        this.setState((state) => ({
            values: { ...state.values, buyFeatureEnabled: value },
        }));
    };

    onDynamicGameConfigurationChange = (value: boolean) => {
        this.setState((state) => ({
            values: { ...state.values, dynamicGameConfigurationEnabled: value },
        }));
    };

    onGeolocationChange = (value: boolean) => {
        this.setState((state) => ({
            values: { ...state.values, geolocationEnabled: value },
        }));
    };

    onSingleSessionModeChange = (value: boolean) => {
        this.setState((state) => ({
            values: { ...state.values, singleSessionModeEnabled: value },
        }));
    };

    onGameEventsSupportChange = (value: boolean) => {
        this.confirmToggle('gameEventsEnabled', 'Game Events', value);
    };

    componentWillUnmount() {
        Modal.destroyAll();
    }

    render = () => {
        const { t, isExistingBrand, brandData } = this.props;
        return (
            <div className="min-h-screen flex justify-center bg-white rounded-lg mt-2">
                <div className="w-full max-w-2xl p-10">
                    <h2 className="text-center text-xl font-semibold mb-8">
                        {t('edit_brand_info')}
                    </h2>
                    {brandData ? (
                        <>
                            {isExistingBrand && (
                                <div className="mb-6">
                                    <label className="block text-sm font-medium mb-1">
                                        {t('choose_brand')}
                                        <span className="text-red-500 ml-1">* </span>
                                    </label>
                                    <Select
                                        className="w-full h-10 mb-2"
                                        placeholder={this.state.values.name || t('select_brand')}
                                        onChange={this.onChangeBrand}
                                    >
                                        {this.props.operatorData.brands?.map(
                                            (brand: IBrandData) => (
                                                <Option
                                                    key={brand.name}
                                                    value={brand.name}
                                                    selected={brand.name === this.state.values.name}
                                                >
                                                    {brand.name}
                                                </Option>
                                            ),
                                        )}
                                    </Select>
                                    <h3 className="text-lg font-bold mt-2">{brandData.name}</h3>
                                </div>
                            )}
                            <div className="mb-6">
                                <label className="block text-sm font-medium mb-1">
                                    {t('name')}
                                    <span className="text-red-500 ml-1">* </span>
                                </label>
                                <Input
                                    value={this.state.values.name}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        this.onChange(e, 'name')
                                    }
                                    className="w-full h-10"
                                />
                            </div>
                            <div className="border rounded-lg p-4 mb-6 bg-white">
                                <div className="flex items-center mb-4">
                                    <span className="font-semibold text-base mr-2">
                                        Operator Wallet Service
                                    </span>
                                </div>
                                {[
                                    { key: 'serviceUrl', label: 'Service' },
                                    { key: 'serviceLogin', label: 'Service Login' },
                                    { key: 'servicePassword', label: 'Service Password' },
                                    {
                                        key: 'inactivityTimerMinutes',
                                        label: 'Inactivity Timer Minutes',
                                    },
                                ].map((obj, idx, arr) => (
                                    <div
                                        key={obj.key}
                                        className={idx < arr.length - 1 ? 'mb-4' : ''}
                                    >
                                        <label className="block text-sm font-medium mb-1">
                                            {obj.label}
                                            <span className="text-red-500 ml-1">* </span>
                                        </label>
                                        <Input
                                            type={
                                                obj.key === 'inactivityTimerMinutes'
                                                    ? 'number'
                                                    : 'text'
                                            }
                                            min={
                                                obj.key === 'inactivityTimerMinutes' ? 1 : undefined
                                            }
                                            max={
                                                obj.key === 'inactivityTimerMinutes'
                                                    ? 120
                                                    : undefined
                                            }
                                            value={String(
                                                this.state.values[obj.key as keyof IValues],
                                            )}
                                            onChange={
                                                obj.key === 'inactivityTimerMinutes'
                                                    ? (e: React.ChangeEvent<HTMLInputElement>) =>
                                                          this.onInactivityTimerChange(
                                                              e.target.value,
                                                          )
                                                    : (e: React.ChangeEvent<HTMLInputElement>) =>
                                                          this.onChange(e, obj.key as brandDataKeys)
                                            }
                                            className="w-full h-10"
                                        />
                                    </div>
                                ))}
                            </div>
                            {brandData.id ? (
                                <div className="mb-6">
                                    <label className="block text-sm font-medium mb-1">
                                        Public/RGS API
                                        <span className="text-red-500 ml-1">* </span>
                                    </label>
                                    <div className="flex gap-2 items-center">
                                        <Input
                                            placeholder="Username"
                                            value={this.state.username}
                                            onChange={(e) =>
                                                this.setState({ username: e.target.value })
                                            }
                                            className="w-40 h-10"
                                        />
                                        <Input
                                            placeholder="Password"
                                            value={this.props.freeRoundsCredentials.password}
                                            disabled
                                            className="w-40 h-10"
                                        />
                                        {this.props.freeRoundsCredentials.password && (
                                            <Button
                                                shape="round"
                                                onClick={this.copyToClipboard}
                                                icon={<CopyOutlined />}
                                            />
                                        )}
                                        <Button
                                            shape="round"
                                            disabled={!brandData.id}
                                            onClick={this.generatePassword}
                                        >
                                            <span className="flex items-center gap-1">
                                                Generate{' '}
                                                {this.props.credentialsLoading ? (
                                                    <LoadingOutlined />
                                                ) : null}
                                            </span>
                                        </Button>
                                        {this.props.freeRoundsCredentials.password && (
                                            <Button
                                                shape="round"
                                                type="primary"
                                                onClick={this.savePassword}
                                            >
                                                Save
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ) : null}
                            <div className="border rounded-lg p-4 mb-6 bg-white">
                                <div className="font-bold text-base mb-3">Promotions</div>
                                <div className="border rounded-lg overflow-hidden">
                                    <div className="flex items-center justify-between px-4 py-2 border-b last:border-b-0">
                                        <span className="flex items-center gap-1">Free Rounds</span>
                                        <Switch
                                            checked={this.state.values.freeRoundsEnabled}
                                            onChange={this.onFreeRoundsSupportChange}
                                        />
                                    </div>
                                    <div className="flex items-center justify-between px-4 py-2 border-b last:border-b-0">
                                        <span className="flex items-center gap-1">
                                            Awarded Feature
                                        </span>
                                        <Switch
                                            checked={this.state.values.awardedFeatureEnabled}
                                            onChange={this.onAwardedFeatureSupportChange}
                                        />
                                    </div>
                                    <div className="flex items-center justify-between px-4 py-2 border-b last:border-b-0">
                                        <span className="flex items-center gap-1">
                                            Game Events
                                        </span>
                                        <Switch
                                            checked={this.state.values.gameEventsEnabled}
                                            onChange={this.onGameEventsSupportChange}
                                        />
                                    </div>
                                    <div className="flex items-center justify-between px-4 py-2 border-b last:border-b-0">
                                        <span className="flex items-center gap-1">
                                            Promotion Type/Flow
                                        </span>
                                        {/*<Select*/}
                                        {/*    className="w-32"*/}
                                        {/*    value={this.state.values.freeRoundPromotionType}*/}
                                        {/*    onChange={(value) =>*/}
                                        {/*        this.setState((state) => ({*/}
                                        {/*            values: {*/}
                                        {/*                ...state.values,*/}
                                        {/*                freeRoundPromotionType: value,*/}
                                        {/*            },*/}
                                        {/*        }))*/}
                                        {/*    }*/}
                                        {/*>*/}
                                        {/*    <Option value={0}>Primary</Option>*/}
                                        {/*    <Option value={1}>Secondary</Option>*/}
                                        {/*</Select>*/}
                                        {this.state.values.freeRoundPromotionType !== null &&
                                        this.state.values.freeRoundPromotionType !== undefined ? (
                                            <span>
                                                {this.state.values.freeRoundPromotionType.toString()}{' '}
                                                {`(${this.state.values.freeRoundPromotionType === 0 ? 'Primary' : 'Secondary'})`}
                                            </span>
                                        ) : null}
                                    </div>
                                    <div className="flex items-center justify-between px-4 py-2">
                                        <span className="flex items-center gap-1">
                                            Promotion Direct
                                        </span>
                                        <span>
                                            {this.state.values.directFreeRoundsSupporting.toString()}{' '}
                                            {`(${this.state.values.directFreeRoundsSupporting ? 'Direct' : 'Reversed'})`}
                                        </span>
                                        {/*<Select*/}
                                        {/*    value={this.state.values.directFreeRoundsSupporting}*/}
                                        {/*    onChange={(value) =>*/}
                                        {/*        this.setState((state) => ({*/}
                                        {/*            values: {*/}
                                        {/*                ...state.values,*/}
                                        {/*                directFreeRoundsSupporting: value,*/}
                                        {/*            },*/}
                                        {/*        }))*/}
                                        {/*    }*/}
                                        {/*    className="w-32"*/}
                                        {/*>*/}
                                        {/*    <Option value={true}>Direct</Option>*/}
                                        {/*    <Option value={false}>Reversed</Option>*/}
                                        {/*</Select>*/}
                                    </div>
                                </div>
                            </div>
                            <div className="mb-8">
                                <div className="border rounded-lg bg-white flex items-center justify-between px-4 py-3 mb-4">
                                    <span className="font-medium flex items-center gap-1">
                                        Buy Feature
                                    </span>
                                    <Switch
                                        checked={this.state.values.buyFeatureEnabled}
                                        onChange={this.onBuyFeatureSupportChange}
                                    />
                                </div>
                                <div className="border rounded-lg bg-white flex items-center justify-between px-4 py-3 mb-4">
                                    <span className="font-medium flex items-center gap-1">
                                        Dynamic Game Configuration
                                    </span>
                                    <Switch
                                        checked={this.state.values.dynamicGameConfigurationEnabled}
                                        onChange={this.onDynamicGameConfigurationChange}
                                    />
                                </div>
                                <div className="border rounded-lg bg-white flex items-center justify-between px-4 py-3 mb-4">
                                    <span className="font-medium flex items-center gap-1">
                                        Geolocation
                                    </span>
                                    <Switch
                                        checked={this.state.values.geolocationEnabled}
                                        onChange={this.onGeolocationChange}
                                    />
                                </div>
                                <div className="border rounded-lg bg-white flex items-center justify-between px-4 py-3">
                                    <span className="font-medium flex items-center gap-1">
                                        Single Session Mode
                                    </span>
                                    <Switch
                                        checked={this.state.values.singleSessionModeEnabled}
                                        onChange={this.onSingleSessionModeChange}
                                    />
                                </div>
                            </div>
                            <div className="flex gap-4 mt-4 justify-center">
                                {isExistingBrand && (
                                    <Button
                                        shape="round"
                                        size="large"
                                        disabled={!brandData.id}
                                        onClick={this.nextStep}
                                    >
                                        {t('next')}
                                    </Button>
                                )}
                                <Button
                                    shape="round"
                                    size="large"
                                    type="primary"
                                    disabled={!this.isDataReady()}
                                    onClick={this.handleSubmit}
                                >
                                    {t('continue_save')}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <Loader />
                    )}
                </div>
            </div>
        );
    };
}

const mapStateToProps = (state: any) => ({
    loading: isBrandDataLoading(state),
    freeRoundsCredentials: getFreeRoundsCredentials(state),
    credentialsLoading: getFreeRoundsCredentialsLoadingStatus(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    addBrandInfo: (data: IValues) => dispatch(addBrandDataRequest(data)),
    updateBrandInfo: (data: IValues) => dispatch(updateBrandDataRequest(data)),
    generateFreeRoundsCredentials: (data: any) =>
        dispatch(generateFreeRoundsCredentialsRequest(data)),
    saveFreeRoundsCredentials: (data: any) => dispatch(saveFreeRoundsCredentialsRequest(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(StepManageBrandInfo));
